import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../utils/supabaseClient';
import AddSubfamilia from './AddSubfamilia'; 
import AdminNavBar from '../../components/AdminNavBar';
import { uploadSubfamiliaThumbnail } from '../../utils/imageUploadUtils';
import PaginationBar from '../../components/PaginationBar';
import './styles.css';

function EditSubfamilia() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subfamilias, setSubfamilias] = useState([]);
  const [totalSubfamilias, setTotalSubfamilias] = useState(0);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', familia_id: '', thumbnail: '', hidden: false });
  const [familias, setFamilias] = useState([]);
  const [message, setMessage] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const imagesPerPage = 6; // Set the number of thumbnails per page

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting user:', error);
      } else {
        setUser(user);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN') {
          setUser(session?.user || null);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchSubfamilias(currentPage, search);
    }
    // eslint-disable-next-line
  }, [user, currentPage, search]);

  const subfamiliasPerPage = 10;
  const fetchSubfamilias = async (page = 1, searchQuery = '') => {
    // Calculate range for pagination
    const from = (page - 1) * subfamiliasPerPage;
    const to = from + subfamiliasPerPage - 1;

    let query = supabase
      .from('subfamilia')
      .select('*', { count: 'exact' })
      .range(from, to)
      .order('id', { ascending: false });

    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase().trim();
      query = query.or(`nombre.ilike.%${searchLower}%,familia_id.ilike.%${searchLower}%`);
    }

    const { data, count, error } = await query;
    if (!error) {
      setSubfamilias(data || []);
      setTotalSubfamilias(count || 0);
    }
    const { data: familias } = await supabase.from('familia').select('id, nombre');
    setFamilias(familias || []);
  };

  // Refresh subfamilias after adding
  const handleCloseAdd = () => {
    setShowAdd(false);
    fetchSubfamilias(currentPage, search);
  };

  function handleEdit(sub) {
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para editar subfamilias');
      return;
    }
    
    setEditing(sub.id);
    setForm({
      nombre: sub.nombre,
      familia_id: sub.familia_id,
      thumbnail: sub.thumbnail || '',
      hidden: sub.hidden || false
    });
    setMessage('');
  }

  async function handleSave(e) {
    e.preventDefault();
    
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para guardar cambios');
      return;
    }
    
    if (!form.thumbnail) {
      setMessage('Error: Thumbnail URL is missing');
      return;
    }
    const { error } = await supabase
      .from('subfamilia')
      .update({
        nombre: form.nombre,
        familia_id: form.familia_id,
        thumbnail: form.thumbnail,
        hidden: form.hidden
      })
      .eq('id', editing);
    if (!error) {
      setMessage('¡Actualizado!');
      setEditing(null);
      const { data: subfamilias } = await supabase.from('subfamilia').select('*');
      setSubfamilias(subfamilias || []);
    } else {
      setMessage('Error al actualizar');
    }
  }

  // Fetch thumbnails from bucket (same as AddSubfamilia)
  useEffect(() => {
    async function fetchAllThumbnails(path = 'thumbnails', limit = imagesPerPage, offset = 0) {
      let allFiles = [];
      const { data, error } = await supabase
        .storage
        .from('productsamples')
        .list(path, { limit, offset });
      if (error) return [];
      for (const item of data) {
        if (item.metadata) {
          allFiles.push({ name: item.name, path: `${path}/${item.name}` });
        } else {
          const subFiles = await fetchAllThumbnails(`${path}/${item.name}`, limit, offset);
          allFiles = allFiles.concat(subFiles);
        }
      }
      return allFiles;
    }

    fetchAllThumbnails().then(files => {
      setThumbnails(
        files
          .filter(f => f.name.match(/\.(jpg|jpeg|png|webp|png)$/i))
          .map(f => ({
            ...f,
            path: supabase.storage.from('productsamples').getPublicUrl(f.path).data.publicUrl
          }))
      );
    });
  }, []);

  useEffect(() => {
    async function fetchThumbnailsForPage(page = 1) {
      const offset = (page - 1) * imagesPerPage;
      const { data, error } = await supabase
        .storage
        .from('productsamples')
        .list('thumbnails', { limit: imagesPerPage, offset });
      if (error) return [];
      return data
        .filter(f => f.name.match(/\.(jpg|jpeg|png|webp|png)$/i))
        .map(f => ({
          ...f,
          path: supabase.storage.from('productsamples').getPublicUrl(`thumbnails/${f.name}`).data.publicUrl
        }));
    }

    fetchThumbnailsForPage(currentPage).then(files => {
      setThumbnails(files);
    });
  }, [currentPage]);

  // Pagination logic for thumbnails
  const totalPages = Math.ceil(thumbnails.length / imagesPerPage);
  const thumbnailsToShow = thumbnails; // Already fetched for the current page

  // Handler for thumbnail upload using utility function
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const result = await uploadSubfamiliaThumbnail(file);
      
      if (result.success) {
        setForm((f) => ({ ...f, thumbnail: result.url }));
        setThumbnails((prevThumbnails) => [
          ...prevThumbnails,
          { name: result.fileName, path: result.url },
        ]);
        setMessage(result.message);
      } else {
        setMessage(result.error);
        alert('Error al subir imagen: ' + result.error);
      }
    } catch (err) {
      const errorMsg = 'Error inesperado al subir imagen';
      setMessage(errorMsg);
      alert(errorMsg);
    } finally {
      setUploading(false);
    }
  };


  // Pagination for subfamilias
  const subfamiliaTotalPages = Math.ceil(totalSubfamilias / subfamiliasPerPage);

  if (authLoading) {
    return <div className="loading">Cargando...</div>;
  }

  // If user is not authenticated, show login message
  if (!user) {
    return (
      <>
        <AdminNavBar user={user} />
        <div className="page-container">
          <div className="auth-message">
            <h2>Acceso Restringido</h2>
            <p>Debes iniciar sesión para acceder a la gestión de subfamilias.</p>
            <button 
              onClick={() => navigate('/admin')}
              className="primary-button"
            >
              Ir a Iniciar Sesión
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavBar user={user} />
      <div className="page-container">
        <div className="page-header">
          <h2>Editar Subfamilia</h2>
          <div className="header-actions">
            {user && (
              <span className="user-info">
                Conectado como: {user.email}
              </span>
            )}
            <button
              onClick={() => setShowAdd(true)}
              className="primary-button"
            >
              Agregar Subfamilia
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className='search-container'>
          <input
            type="text"
            placeholder="Buscar por nombre o familia..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Popup Modal using AddSubfamilia as content */}
        {showAdd && (
          <div className='popup'>
            <div className='popup-content'>
              <button
                onClick={handleCloseAdd}
                className='popup-close'
                title="Cerrar"
              >×</button>
              <AddSubfamilia lang="es" onClose={handleCloseAdd} onAdded={handleCloseAdd} />
            </div>
          </div>
        )}

        {/* Edit popup */}
        {editing && (
          <div className='popup'>
            <div className='popup-content' >
              <button
                onClick={() => setEditing(null)}
                className='popup-close'
                title="Cerrar"
              >×</button>
              <form onSubmit={handleSave} style={{ marginBottom: 0 }}>
                <div style={{ marginBottom: 10 }}>
                  <label>Nombre:&nbsp;
                    <input
                      value={form.nombre}
                      onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                      required
                    />
                  </label>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label>Familia:&nbsp;
                    <select
                      value={form.familia_id}
                      onChange={e => setForm(f => ({ ...f, familia_id: e.target.value }))}
                      required
                    >
                      <option value="">Seleccione una familia</option>
                      {familias.map(f => (
                        <option key={f.id} value={f.id}>{f.nombre}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.hidden}
                      onChange={e => setForm(f => ({ ...f, hidden: e.target.checked }))}
                    />
                    Ocultar del catálogo
                  </label>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label>Thumbnail:&nbsp;</label>
                  <div style={{ margin: '10px 0' }}>
                    {form.thumbnail && (
                      <img
                        src={form.thumbnail}
                        alt="thumbnail"
                        className='thumbnail-container'
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                    {thumbnailsToShow.map(img => (
                      <img
                        key={img.path}
                        src={img.path}
                        alt={img.name}
                        className='thumbnail'
                        onClick={() => setForm(f => ({ ...f, thumbnail: img.path }))}
                        title={img.name}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className='left-arrow-btn'
                    >
                      &lt;
                    </button>
                    <span style={{ alignSelf: 'center' }}>
                      {currentPage} / {totalPages || 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className='pagination-btn'
                    >
                      &gt;
                    </button>
                  </div>
                  <div style={{ margin: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    
                    <label
                      htmlFor="thumbnail-upload-edit"
                      className='primary-button'
                    >
                      {uploading ? 'Subiendo...' : 'Elegir archivo'}
                      <input
                        id="thumbnail-upload-edit"
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        disabled={uploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                </div>
                <button type="submit" className='secondary-button'>Guardar</button>
                <button type="button" className='danger-button'onClick={() => setEditing(null)}>Cancelar</button>
                {message && <div style={{ marginTop: 10, color: message === '¡Actualizado!' ? 'green' : 'red' }}>{message}</div>}
              </form>
            </div>
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Familia</th>
              <th>Thumbnail</th>
              <th>Oculto</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {subfamilias.map(sub => (
              <tr key={sub.id}>
                <td >{sub.id}</td>
                <td >{sub.nombre}</td>
                <td >
                  {familias.find(f => f.id === sub.familia_id)?.nombre || ''}
                </td>
                <td>
                  {sub.thumbnail && <img src={sub.thumbnail} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={sub.hidden || false}
                    onChange={async (e) => {
                      if (!user) {
                        setMessage('Debes iniciar sesión para modificar subfamilias');
                        return;
                      }
                      const newHiddenValue = e.target.checked;
                      const { error } = await supabase
                        .from('subfamilia')
                        .update({ hidden: newHiddenValue })
                        .eq('id', sub.id);
                      if (!error) {
                        // Update local state
                        setSubfamilias(subfamilias.map(s => 
                          s.id === sub.id ? { ...s, hidden: newHiddenValue } : s
                        ));
                      } else {
                        setMessage('Error al actualizar');
                      }
                    }}
                    style={{ cursor: 'pointer', width: 18, height: 18 }}
                  />
                </td>
                <td>
                  <button
                    onClick={() => handleEdit(sub)}
                    className='secondary-button'
                  >
                    Editar
                  </button>
                  <button
                    onClick={async () => {
                      // Check authentication
                      if (!user) {
                        setMessage('Debes iniciar sesión para eliminar subfamilias');
                        return;
                      }
                      
                      const confirm1 = window.confirm(
                        `¿Seguro que desea eliminar la subfamilia "${sub.nombre}"?\nEsta acción no se puede deshacer.`
                      );
                      if (!confirm1) return;
                      const confirm2 = window.prompt(
                        `Para confirmar, escriba el nombre exacto de la subfamilia: "${sub.nombre}"`
                      );
                      if (confirm2 !== sub.nombre) {
                        setMessage('El nombre no coincide. Cancelando eliminación.');
                        return;
                      }
                      const { error } = await supabase
                        .from('subfamilia')
                        .delete()
                        .eq('id', sub.id);
                      if (!error) {
                        setSubfamilias(subfamilias.filter(s => s.id !== sub.id));
                      } else {
                        setMessage('Error al eliminar');
                      }
                    }}
                    className='danger-button'
                    
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar
          currentPage={currentPage}
          totalItems={totalSubfamilias}
          itemsPerPage={subfamiliasPerPage}
          onPageChange={page => setCurrentPage(page)}
          label="subfamilias"
        />
      </div>
    </>
  );
}

export default EditSubfamilia;